import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClockCounterClockwise } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/login-report", label: "Login Report", icon: <ClockCounterClockwise size={18} weight="duotone" />, testId: "nav-admin-login-report" }];

export default function AdminLoginReportPage() {
  const { user } = useAuth();
  const [reps, setReps] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/reports/login-report")
      .then(({ data }) => { setReps(data.reps); setNote(data.note); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout navItems={navItems} moduleName="Login Report" moduleBackPath={user?.role === "manager" ? "/manager" : "/admin"}>
      <div data-testid="admin-login-report-page">
        <div className="mb-6">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Shows exactly when each rep last logged in, and flags anyone who has never logged in at all.">All-Time Login Report</h1>
          <p className="text-igc-muted text-sm">Every rep, sorted by "Never logged in" first, then most recent login. Reps created Sep 15-16, 2026 are excluded.</p>
        </div>

        {note && (
          <div className="mb-6 bg-igc-gold/5 border border-igc-gold/20 p-4 text-igc-muted text-xs leading-relaxed" data-testid="admin-login-report-note">
            {note}
          </div>
        )}

        {loading && <p className="text-igc-muted text-sm" data-testid="admin-login-report-loading">Loading login report...</p>}

        {!loading && reps.length === 0 && (
          <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid="admin-login-report-empty">No reps found.</p>
        )}

        {!loading && reps.length > 0 && (
          <div className="bg-igc-surface border border-igc-gold/15" data-testid="admin-login-report-table">
            <div className="grid grid-cols-5 gap-3 px-4 py-3 border-b border-igc-gold/15 text-igc-muted text-[10px] uppercase tracking-widest">
              <span>Rep</span>
              <span>Status</span>
              <span>Last Login (All-Time)</span>
              <span>Logins Tracked (Since Sep 13)</span>
              <span>Account Created</span>
            </div>
            {reps.map((r) => (
              <div
                key={r.id}
                className={`grid grid-cols-5 gap-3 px-4 py-3 border-b border-igc-gold/10 items-center text-sm ${r.never_logged_in ? "bg-igc-danger/5" : ""}`}
                data-testid={`login-report-row-${r.user_id}`}
              >
                <span className="text-igc-cream" data-testid={`login-report-name-${r.user_id}`}>{r.display_name} <span className="text-igc-muted text-xs">({r.user_id})</span></span>
                <span data-testid={`login-report-status-${r.user_id}`}>
                  {r.never_logged_in ? (
                    <span className="text-[10px] px-2 py-1 uppercase bg-igc-danger/10 text-igc-danger border border-igc-danger/30">Never Logged In</span>
                  ) : (
                    <span className="text-[10px] px-2 py-1 uppercase bg-igc-success/10 text-igc-success border border-igc-success/30">Active</span>
                  )}
                </span>
                <span className="text-igc-muted text-xs" data-testid={`login-report-last-login-${r.user_id}`}>{r.last_login_at ? formatET(r.last_login_at, "MMM d, yyyy h:mm a") + " ET" : "—"}</span>
                <span className="text-igc-muted text-xs" data-testid={`login-report-tracked-count-${r.user_id}`}>{r.logins_tracked_since_sep13}</span>
                <span className="text-igc-muted text-xs" data-testid={`login-report-created-${r.user_id}`}>{r.created_at ? formatET(r.created_at, "MMM d, yyyy") : "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
